import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import * as projectService from '../services/projectService';
import * as aiInsightsService from '../services/aiInsightsService';
import type { Project } from '../services/projectService';
import type {
  ScorecardData,
  RiskData,
  TestCaseData,
  GapAnalysisData,
  AllInsightsData
} from '../services/aiInsightsService';

/**
 * AI Insights Dashboard
 *
 * Unified dashboard for AI-powered project insights:
 * - Requirements Scorecard
 * - Risk Identification
 * - Test Case Generation
 * - Gap Analysis
 * - Quick links to existing AI features
 */
export function AIInsightsDashboard(): React.ReactElement {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();

  // Project state
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Insights summary
  const [insightsSummary, setInsightsSummary] = useState<AllInsightsData | null>(null);

  // Individual insight data and loading states
  const [scorecard, setScorecard] = useState<ScorecardData | null>(null);
  const [scorecardLoading, setScorecardLoading] = useState(false);
  const [scorecardError, setScorecardError] = useState('');

  const [risks, setRisks] = useState<RiskData | null>(null);
  const [risksLoading, setRisksLoading] = useState(false);
  const [risksError, setRisksError] = useState('');

  const [testCases, setTestCases] = useState<TestCaseData | null>(null);
  const [testCasesLoading, setTestCasesLoading] = useState(false);
  const [testCasesError, setTestCasesError] = useState('');

  const [gapAnalysis, setGapAnalysis] = useState<GapAnalysisData | null>(null);
  const [gapAnalysisLoading, setGapAnalysisLoading] = useState(false);
  const [gapAnalysisError, setGapAnalysisError] = useState('');

  // Gap-to-requirement conversion state
  const [convertingGapIndex, setConvertingGapIndex] = useState<number | null>(null);
  const [gapConvertSuccess, setGapConvertSuccess] = useState('');
  const [gapConvertError, setGapConvertError] = useState('');

  // Expanded card state
  const [expandedCard, setExpandedCard] = useState<string | null>(null);

  // Load project and insights summary
  useEffect(() => {
    const loadData = async () => {
      if (!token || !projectId) return;

      setLoading(true);
      setError('');

      try {
        const projectData = await projectService.getProjectById(projectId, token);
        setProject(projectData.project);

        const summary = await aiInsightsService.getAllInsights(projectId, token);
        setInsightsSummary(summary);

        // Load any existing insights
        if (summary.insights.scorecard) {
          try {
            const scorecardData = await aiInsightsService.getScorecard(projectId, token);
            setScorecard(scorecardData);
          } catch { /* No existing scorecard */ }
        }
        if (summary.insights.risks) {
          try {
            const risksData = await aiInsightsService.getRisks(projectId, token);
            setRisks(risksData);
          } catch { /* No existing risks */ }
        }
        if (summary.insights.test_cases) {
          try {
            const testCasesData = await aiInsightsService.getTestCases(projectId, token);
            setTestCases(testCasesData);
          } catch { /* No existing test cases */ }
        }
        if (summary.insights.gap_analysis) {
          try {
            const gapData = await aiInsightsService.getGapAnalysis(projectId, token);
            setGapAnalysis(gapData);
          } catch { /* No existing gap analysis */ }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, token]);

  // Generate scorecard handler
  const handleGenerateScorecard = useCallback(async () => {
    if (!token || !projectId) return;

    setScorecardLoading(true);
    setScorecardError('');

    try {
      const result = await aiInsightsService.generateScorecard(projectId, token);
      setScorecard(result);
      // Refresh summary
      const summary = await aiInsightsService.getAllInsights(projectId, token);
      setInsightsSummary(summary);
    } catch (err: any) {
      setScorecardError(err.response?.data?.error || err.message || 'Failed to generate scorecard');
    } finally {
      setScorecardLoading(false);
    }
  }, [projectId, token]);

  // Identify risks handler
  const handleIdentifyRisks = useCallback(async () => {
    if (!token || !projectId) return;

    setRisksLoading(true);
    setRisksError('');

    try {
      const result = await aiInsightsService.identifyRisks(projectId, token);
      setRisks(result);
      const summary = await aiInsightsService.getAllInsights(projectId, token);
      setInsightsSummary(summary);
    } catch (err: any) {
      setRisksError(err.response?.data?.error || err.message || 'Failed to identify risks');
    } finally {
      setRisksLoading(false);
    }
  }, [projectId, token]);

  // Generate test cases handler
  const handleGenerateTestCases = useCallback(async () => {
    if (!token || !projectId) return;

    setTestCasesLoading(true);
    setTestCasesError('');

    try {
      const result = await aiInsightsService.generateTestCases(projectId, token);
      setTestCases(result);
      const summary = await aiInsightsService.getAllInsights(projectId, token);
      setInsightsSummary(summary);
    } catch (err: any) {
      setTestCasesError(err.response?.data?.error || err.message || 'Failed to generate test cases');
    } finally {
      setTestCasesLoading(false);
    }
  }, [projectId, token]);

  // Analyze gaps handler
  const handleAnalyzeGaps = useCallback(async () => {
    if (!token || !projectId) return;

    setGapAnalysisLoading(true);
    setGapAnalysisError('');

    try {
      const result = await aiInsightsService.analyzeGaps(projectId, token);
      setGapAnalysis(result);
      const summary = await aiInsightsService.getAllInsights(projectId, token);
      setInsightsSummary(summary);
    } catch (err: any) {
      setGapAnalysisError(err.response?.data?.error || err.message || 'Failed to analyze gaps');
    } finally {
      setGapAnalysisLoading(false);
    }
  }, [projectId, token]);

  // Export test cases
  const handleExportTestCases = useCallback((format: 'csv' | 'json') => {
    if (!testCases || !project) return;

    const filename = `${project.project_code || project.name}_test_cases.${format}`;

    if (format === 'csv') {
      const content = aiInsightsService.exportTestCasesToCSV(testCases);
      aiInsightsService.downloadFile(content, filename, 'text/csv');
    } else {
      const content = aiInsightsService.exportTestCasesToJSON(testCases);
      aiInsightsService.downloadFile(content, filename, 'application/json');
    }
  }, [testCases, project]);

  // Create requirement from gap handler
  const handleCreateRequirementFromGap = useCallback(async (gapIndex: number) => {
    if (!token || !projectId) return;

    setConvertingGapIndex(gapIndex);
    setGapConvertSuccess('');
    setGapConvertError('');

    try {
      const result = await aiInsightsService.createRequirementFromGap(projectId, gapIndex, token);
      // Update local gap analysis state to mark the gap as converted
      setGapAnalysis(prev => {
        if (!prev) return prev;
        const updatedGaps = [...prev.gaps];
        updatedGaps[gapIndex] = { ...updatedGaps[gapIndex], converted_to_requirement_id: result.requirement_id };
        return { ...prev, gaps: updatedGaps };
      });
      setGapConvertSuccess(`Requirement ${result.requirement_code} created: "${result.title}"`);
      setTimeout(() => setGapConvertSuccess(''), 5000);
    } catch (err: any) {
      setGapConvertError(err.response?.data?.error || err.message || 'Failed to create requirement');
      setTimeout(() => setGapConvertError(''), 5000);
    } finally {
      setConvertingGapIndex(null);
    }
  }, [projectId, token]);

  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  // Get score color
  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600 dark:text-green-400';
    if (score >= 6) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 4) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  // Get risk severity color
  const getRiskColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  // Get coverage color
  const getCoverageColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-gray-500 dark:text-gray-400">Loading AI Insights...</div>
        </div>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-red-600 dark:text-red-400">{error || 'Project not found'}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-500 rounded-lg shadow-lg mb-6 p-6">
          <div className="flex justify-between items-center">
            <div>
              <button
                onClick={() => navigate(`/projects/${projectId}`)}
                className="text-sm text-purple-100 hover:text-white mb-2 inline-flex items-center transition-colors"
              >
                ← Back to Project
              </button>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                AI Insights
              </h1>
              <p className="text-purple-100 mt-1">{project.name}</p>
            </div>
            <div className="text-right">
              <div className="text-sm text-purple-100">Project Code</div>
              <div className="text-lg font-semibold text-white">{project.project_code}</div>
            </div>
          </div>
        </div>

        {/* Main Grid - 2x2 for insight cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Requirements Scorecard Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Requirements Scorecard</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Quality assessment across 4 dimensions</p>
                </div>
              </div>
              <button
                onClick={handleGenerateScorecard}
                disabled={scorecardLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {scorecardLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </span>
                ) : scorecard ? 'Regenerate' : 'Generate'}
              </button>
            </div>

            {scorecardError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{scorecardError}</p>
              </div>
            )}

            {scorecard ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className={`text-4xl font-bold ${getScoreColor(scorecard.overall_score)}`}>
                      {scorecard.overall_score.toFixed(1)}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500 text-lg">/10</span>
                  </div>
                  <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                    <div>{scorecard.requirement_scores.length} requirements scored</div>
                    <div>{formatRelativeTime(scorecard.generated_at)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  {Object.entries(scorecard.dimension_averages).map(([key, value]) => (
                    <div key={key} className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                      <div className="text-xs text-gray-500 dark:text-gray-400 uppercase">{key}</div>
                      <div className={`text-lg font-semibold ${getScoreColor(value)}`}>{value.toFixed(1)}</div>
                    </div>
                  ))}
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300">{scorecard.summary}</p>

                <button
                  onClick={() => setExpandedCard(expandedCard === 'scorecard' ? null : 'scorecard')}
                  className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {expandedCard === 'scorecard' ? 'Show Less' : 'View Details'}
                </button>

                {expandedCard === 'scorecard' && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 max-h-64 overflow-y-auto">
                    {scorecard.requirement_scores.slice(0, 10).map((req, idx) => (
                      <div key={req.requirement_id} className="mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="font-medium text-sm text-gray-900 dark:text-white">{req.requirement_title}</div>
                        <div className="flex gap-2 mt-1">
                          {Object.entries(req.scores).map(([k, v]) => (
                            <span key={k} className={`text-xs px-2 py-0.5 rounded ${getScoreColor(v)} bg-gray-100 dark:bg-gray-600`}>
                              {k.charAt(0).toUpperCase()}: {v}
                            </span>
                          ))}
                        </div>
                        {req.recommendations.length > 0 && (
                          <div className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                            {req.recommendations[0]}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p>Click Generate to score your requirements</p>
              </div>
            )}
          </div>

          {/* Risk Identification Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Risk Identification</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Potential risks with mitigation strategies</p>
                </div>
              </div>
              <button
                onClick={handleIdentifyRisks}
                disabled={risksLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {risksLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </span>
                ) : risks ? 'Regenerate' : 'Identify'}
              </button>
            </div>

            {risksError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{risksError}</p>
              </div>
            )}

            {risks ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-2">
                    {risks.risk_matrix_summary.critical > 0 && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor('critical')}`}>
                        {risks.risk_matrix_summary.critical} Critical
                      </span>
                    )}
                    {risks.risk_matrix_summary.high > 0 && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor('high')}`}>
                        {risks.risk_matrix_summary.high} High
                      </span>
                    )}
                    {risks.risk_matrix_summary.medium > 0 && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor('medium')}`}>
                        {risks.risk_matrix_summary.medium} Medium
                      </span>
                    )}
                    {risks.risk_matrix_summary.low > 0 && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getRiskColor('low')}`}>
                        {risks.risk_matrix_summary.low} Low
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {formatRelativeTime(risks.generated_at)}
                  </div>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{risks.summary}</p>

                <button
                  onClick={() => setExpandedCard(expandedCard === 'risks' ? null : 'risks')}
                  className="text-sm text-red-600 dark:text-red-400 hover:underline"
                >
                  {expandedCard === 'risks' ? 'Show Less' : `View All ${risks.risks.length} Risks`}
                </button>

                {expandedCard === 'risks' && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 max-h-64 overflow-y-auto">
                    {risks.risks.map((risk, idx) => (
                      <div key={idx} className="mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-gray-900 dark:text-white">{risk.title}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${getRiskColor(risk.risk_score >= 15 ? 'critical' : risk.risk_score >= 10 ? 'high' : risk.risk_score >= 5 ? 'medium' : 'low')}`}>
                            Score: {risk.risk_score}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{risk.description}</p>
                        <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
                          <strong>Mitigation:</strong> {risk.mitigation}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p>Click Identify to analyze project risks</p>
              </div>
            )}
          </div>

          {/* Test Case Generator Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Test Case Generator</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Automated test cases from requirements</p>
                </div>
              </div>
              <button
                onClick={handleGenerateTestCases}
                disabled={testCasesLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {testCasesLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </span>
                ) : testCases ? 'Regenerate' : 'Generate'}
              </button>
            </div>

            {testCasesError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{testCasesError}</p>
              </div>
            )}

            {testCases ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                      {testCases.summary.total_test_cases}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 ml-2">test cases</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleExportTestCases('csv')}
                      className="px-3 py-1 text-xs font-medium text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={() => handleExportTestCases('json')}
                      className="px-3 py-1 text-xs font-medium text-green-600 dark:text-green-400 border border-green-300 dark:border-green-700 rounded-full hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                    >
                      Export JSON
                    </button>
                  </div>
                </div>

                <div className="flex gap-4 mb-4">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Functional: {testCases.summary.by_type.functional}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Edge: {testCases.summary.by_type.edge_case}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      Negative: {testCases.summary.by_type.negative}
                    </span>
                  </div>
                </div>

                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {formatRelativeTime(testCases.generated_at)}
                </div>

                <button
                  onClick={() => setExpandedCard(expandedCard === 'test_cases' ? null : 'test_cases')}
                  className="mt-3 text-sm text-green-600 dark:text-green-400 hover:underline"
                >
                  {expandedCard === 'test_cases' ? 'Show Less' : 'View Test Cases'}
                </button>

                {expandedCard === 'test_cases' && (
                  <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 max-h-64 overflow-y-auto">
                    {testCases.test_cases.slice(0, 5).map((reqTests, idx) => (
                      <div key={idx} className="mb-4">
                        <div className="font-medium text-sm text-gray-900 dark:text-white mb-2">
                          {reqTests.requirement_title}
                        </div>
                        {reqTests.tests.map((test, testIdx) => (
                          <div key={testIdx} className="ml-4 mb-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${
                                test.test_type === 'functional' ? 'bg-green-500' :
                                test.test_type === 'edge_case' ? 'bg-yellow-500' : 'bg-red-500'
                              }`}></span>
                              <span className="text-xs font-medium text-gray-700 dark:text-gray-200">{test.title}</span>
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Expected: {test.expected_result}
                            </div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p>Click Generate to create test cases</p>
              </div>
            )}
          </div>

          {/* Gap Analysis Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Gap Analysis</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Missing requirements detection</p>
                </div>
              </div>
              <button
                onClick={handleAnalyzeGaps}
                disabled={gapAnalysisLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {gapAnalysisLoading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing...
                  </span>
                ) : gapAnalysis ? 'Regenerate' : 'Analyze'}
              </button>
            </div>

            {gapAnalysisError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{gapAnalysisError}</p>
              </div>
            )}

            {gapAnalysis ? (
              <div>
                {(() => {
                  const remainingGaps = gapAnalysis.gaps.filter(g => !g.converted_to_requirement_id);
                  const convertedCount = gapAnalysis.gaps.length - remainingGaps.length;
                  return (
                    <>
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <span className={`text-4xl font-bold ${getCoverageColor(gapAnalysis.coverage_score)}`}>
                            {gapAnalysis.coverage_score}%
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 ml-2">coverage</span>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{remainingGaps.length}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            gaps remaining{convertedCount > 0 && ` (${convertedCount} converted)`}
                          </div>
                        </div>
                      </div>

                      {gapConvertSuccess && (
                        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <p className="text-sm text-green-600 dark:text-green-400">{gapConvertSuccess}</p>
                        </div>
                      )}

                      {gapConvertError && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                          <p className="text-sm text-red-600 dark:text-red-400">{gapConvertError}</p>
                        </div>
                      )}

                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">{gapAnalysis.summary}</p>

                      <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                        {formatRelativeTime(gapAnalysis.generated_at)}
                      </div>

                      {remainingGaps.length > 0 ? (
                        <>
                          <button
                            onClick={() => setExpandedCard(expandedCard === 'gap_analysis' ? null : 'gap_analysis')}
                            className="text-sm text-purple-600 dark:text-purple-400 hover:underline"
                          >
                            {expandedCard === 'gap_analysis' ? 'Show Less' : `View All ${remainingGaps.length} Gaps`}
                          </button>

                          {expandedCard === 'gap_analysis' && (
                            <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4 max-h-96 overflow-y-auto">
                              {gapAnalysis.gaps.map((gap, idx) => {
                                if (gap.converted_to_requirement_id) return null;
                                return (
                                  <div key={idx} className="mb-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="font-medium text-sm text-gray-900 dark:text-white">{gap.category}</span>
                                      <div className="flex items-center gap-2">
                                        <span className={`text-xs px-2 py-0.5 rounded ${getRiskColor(gap.severity)}`}>
                                          {gap.severity}
                                        </span>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleCreateRequirementFromGap(idx); }}
                                          disabled={convertingGapIndex !== null}
                                          className="px-2 py-0.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                          {convertingGapIndex === idx ? (
                                            <span className="flex items-center gap-1">
                                              <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                              </svg>
                                              Creating...
                                            </span>
                                          ) : 'Create as Requirement'}
                                        </button>
                                      </div>
                                    </div>
                                    <div className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                                      <strong>Missing:</strong> {gap.missing_areas.join(', ')}
                                    </div>
                                    <div className="text-xs text-blue-600 dark:text-blue-400">
                                      <strong>Recommendations:</strong> {gap.recommendations.join('; ')}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                          <svg className="w-8 h-8 mx-auto mb-2 text-green-500 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm">All gaps have been converted to requirements</p>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p>Click Analyze to find requirement gaps</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Access to Existing AI Features */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Access to AI Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link
              to={`/projects/${projectId}`}
              className="flex flex-col items-center p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-indigo-600 dark:text-indigo-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Executive Summary</span>
            </Link>

            <Link
              to={`/projects/${projectId}/requirements`}
              className="flex flex-col items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Requirements Insights</span>
            </Link>

            <Link
              to={`/projects/${projectId}`}
              className="flex flex-col items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-green-600 dark:text-green-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-sm font-medium text-green-700 dark:text-green-300">BRD Generation</span>
            </Link>

            <Link
              to={`/projects/${projectId}/requirements`}
              className="flex flex-col items-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
            >
              <svg className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">AI Chat Assistant</span>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default AIInsightsDashboard;
